import { useMemo } from 'react';
import { VIEW_KEY } from '@vagusx/clients';
import { usePersistentViewFilters } from '@/hooks/use-persistent-view-filters';
import {
  documentsPreferenceToUrl,
  hasAnyFilterParam,
  urlToDocumentsPreference,
} from './document-filter-utils';

interface Args {
  /** Raw URL search params read from the router. */
  search: Record<string, string | undefined>;
  /** Router navigate function — typed loosely to avoid importing router types. */
  navigate: (args: { to: string; search?: Record<string, string>; replace?: boolean }) => void;
  /**
   * When false, the hook is inert — neither hydrates from saved prefs
   * nor saves on URL changes. The dashboard's `Referrals` drilldown
   * sets this to `false` (via `from=dashboard`) so the user's saved
   * documents.list view isn't trampled by transient drilldown filters.
   */
  enabled?: boolean;
}

/**
 * Documents-specific binding of `usePersistentViewFilters`. Mirrors the
 * tasks-inbox wrapper one-for-one — the only differences are the view
 * key, the converters, and the redirect target.
 */
export function usePersistentDocumentFilters({ search, navigate, enabled = true }: Args): {
  waitingForHydrate: boolean;
} {
  const urlSnapshot = useMemo(
    () => urlToDocumentsPreference(search),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable on persistable primitives only
    [
      search.urgency,
      search.classification,
      search.referringProviderName,
      search.healthPlan,
      search.dateFrom,
      search.dateTo,
    ]
  );

  return usePersistentViewFilters({
    viewKey: VIEW_KEY.DOCUMENTS_LIST,
    enabled,
    urlSnapshot,
    urlIsBare: !hasAnyFilterParam(search),
    preferenceToUrl: documentsPreferenceToUrl,
    onHydrateRedirect: (target) => {
      // Preserve the ephemeral search term across the redirect so a fresh
      // page load with `?searchTerm=foo` doesn't drop it when saved
      // filters get hydrated in.
      const merged: Record<string, string> = { ...target };
      if (search.searchTerm) merged.searchTerm = search.searchTerm;
      navigate({ to: '/documents', search: merged, replace: true });
    },
  });
}
